import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ComunicacionAjax } from "../../../../ts/api/comunicacion";
import { BaseModal, ModalCloseCross } from "../../../../components/modals/BaseModal";
import type { Respuesta, Usuario } from "../../../../ts/typings/clases";
import { getSelectionCell, TypedTable } from "../../../../components/TypedTable";
import { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { notificacionTipo } from "../../../../components/Alerts";
import { Loading, QueryError } from "../../../../components/Loading/Loading";

interface ModalMaquinasProps {
	readonly shown: boolean;
	readonly close: () => void;
	readonly usuario: Usuario | null;
}
export function ModalMaquinas(props: ModalMaquinasProps) {
	const [maquinasSeleccionadas, setMaquinasSeleccionadas] = useState<Record<string, boolean>>({});
	const queryClient = useQueryClient();

	const onGuardar = () => {
		if (!props.usuario) return;

		const newMaquinas = Object.entries(maquinasSeleccionadas).filter(([_, selected]) => selected).map(([id]) => id);

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/api/v1/setMaquinaria", {
			"newMaquinas": newMaquinas,
			"idUsuario": props.usuario.id,
		}).then((res) => {
			if (res.tipo != "exito") {
				notificacionTipo("error", res.message ?? "Error al guardar las máquinas");
				void queryClient.invalidateQueries({ queryKey: ["getMaquinasUsuario", props.usuario?.id] });
				return;
			}

			notificacionTipo("success", res.message ?? "Máquinas guardadas con éxito");
			props.close();
			void queryClient.invalidateQueries({ queryKey: ["getMaquinasUsuario", props.usuario?.id] });
		});
	};

	return (
		<BaseModal show={props.shown} close={props.close}>
			<div className="modal-header">
				<ModalCloseCross onClick={props.close} />
				<h4 className="modal-title">Maquina</h4>
			</div>
			<div className="modal-body">
				{props.usuario ? (
					<TablaMaquinasUsuario
						idRepresentante={props.usuario.id}
						selectedMaquinas={maquinasSeleccionadas}
						setSelectedMaquinas={setMaquinasSeleccionadas}
					/>
				) : null}
			</div>
			<div className="modal-footer">
				<button type="button" className="btnSecondary" onClick={props.close}>Cancelar</button>
				<button type="button" className="btnPrimary" onClick={onGuardar}>Guardar</button>
			</div>
		</BaseModal>
	);
}

interface TablaMaquinasUsuarioProps {
	readonly idRepresentante: string,
	readonly selectedMaquinas: Record<string, boolean>;
	readonly setSelectedMaquinas: Setter<Record<string, boolean>>;
}
function TablaMaquinasUsuario(props: TablaMaquinasUsuarioProps) {
	const { selectedMaquinas, setSelectedMaquinas } = props;
	const { data, error, isPending } = UseMaquinasUsuario(props.idRepresentante);

	useEffect(() => {
		if (!data) return;
		const initialSelection: Record<string, boolean> = {};
		data.forEach(maquina => {
			initialSelection[maquina.xoperacion_id] = maquina.check == true;
		});
		setSelectedMaquinas(initialSelection);
	}, [data, setSelectedMaquinas]);

	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<Maquina>();
		return [
			columnHelper.accessor("xoperacion_id", { header: "Id", }),
			columnHelper.accessor("xdescripcion", { header: "Descripción", }),
			columnHelper.accessor("operariosCount", { header: "Nº Operarios", }),
			getSelectionCell(columnHelper, {
				header: "Asignado?",
			}),
		];
	}, []);

	if (error) return <QueryError error={error} fallback="Error al cargar las máquinas" />;
	if (isPending) return <Loading />;

	return (
		<TypedTable
			className="display dataTable"
			data={data}
			columns={columns}

			selection={selectedMaquinas}
			getRowId={row => row.xoperacion_id}
			setSelection={setSelectedMaquinas}
		/>
	);
}


function UseMaquinasUsuario(idRepresentante: string) {
	return useQuery<Maquina[]>({
		queryKey: ["getMaquinasUsuario", idRepresentante],
		queryFn: () => {
			return ComunicacionAjax.sendAjaxRequest<Maquina[]>("GET", "/getMaquinasUsuario", { representante_id: idRepresentante });
		},
	});
}
export interface Maquina {
	xoperacion_id: string,
	xdescripcion: string,
	operariosCount: string,
	check: boolean,
}
